import {Button, Stack} from '@mui/material';
import {
  InvoiceForMailing,
  invoicesQuery,
  invoicesService,
  SendInvoiceForMailing,
  SendInvoiceType
} from '@store/invoices/invoices';
import {useSnackbar} from 'notistack';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {finalize} from 'rxjs';

import ModalComponent from '@components/modal/Modal.component';
import NewTableComponent from '@components/table/NewTable.component';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import ConfirmSendMailModal from '@screens/admin/invoices/bills/send/ConfirmSendMail.modal';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import SelectComponent from '@components/inputs/select/Select.component';

const JobInvoiceDetailsModal = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(true);

  const [invoices, setInvoices] = useState<InvoiceForMailing[]>([]);
  const [invoiceReadyToSend, setInvoiceReadyToSend] = useState<SendInvoiceForMailing[]>([]);
  const [sendInvoiceType, setSendInvoiceType] = useState<SendInvoiceType>(SendInvoiceType.GROUPED_BY_EMAIL);

  const [showConfirm, setShowConfirm] = useState(false);

  const handleClose = useCallback(() => navigate('..', {replace: true}), [navigate]);

  const updateInvoiceForMailing = (id: number, updatedInvoiceData: Partial<InvoiceForMailing>) => {
    setInvoices((prevInvoices) =>
      prevInvoices.map((invoice) =>
        invoice.id === id ? {...invoice, ...updatedInvoiceData} : invoice
      )
    );
  };

  const loadInvoiceReadyToSend = () => {
    const result: SendInvoiceForMailing[] = invoices.map((invoice) => ({
      id: invoice.id,
      emails: invoice.emails,
      ccEmails: invoice.ccEmails
    }));
    setInvoiceReadyToSend(result);
    setShowConfirm(true);
  };

  useEffect(() => {
    const selectedBills = invoicesQuery.selectedBills;
    if (!selectedBills.length) handleClose();
    else {
      invoicesService.getInvoicesToSend(selectedBills).pipe(finalize(() => setLoading(false))).subscribe({
        next: setInvoices,
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
      invoicesService.setSelectedInvoices([]);
    }
  }, [enqueueSnackbar, handleClose]);

  return (
    <>
      <ModalComponent
        maxWidth={false}
        handleClose={handleClose}
        title={t('invoices.bills.mailing.title')}
        content={(
          <Stack pt="30px" pb="25px" height="65vh" width="100%" spacing={2}>
            <NewTableComponent
              loading={loading}
              getRowHeight={() => 150}
              columns={[
                COLUMN_TYPES.MULTI_INPUT({
                  field: 'emails',
                  label: t('invoices.bills.mailing.table.email'),
                  fullWidth: true
                }),
                COLUMN_TYPES.MULTI_INPUT({
                  field: 'ccEmails',
                  label: t('invoices.bills.mailing.table.ccEmails'),
                  fullWidth: true
                }),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'invoice.invoiceRef',
                  label: t('invoices.bills.mailing.table.bill'),
                  valueGetter: (params: GridValueGetterParams<any, InvoiceForMailing>) => params.row.invoice.invoiceRef,
                  width: 150,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'invoice.clientName',
                  color: 'secondary',
                  label: t('invoices.bills.mailing.table.client'),
                  valueGetter: (params: GridValueGetterParams<any, InvoiceForMailing>) =>
                    params.row.invoice.clientCount.length > 1 ? t('global.multiple', {count: params.row.invoice.clientCount.length}) : params.row.invoice.clientName,
                  minWidth: 200,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'invoice.unitName',
                  color: 'secondary',
                  label: t('invoices.bills.mailing.table.clientSite'),
                  valueGetter: (params: GridValueGetterParams<any, InvoiceForMailing>) =>
                    params.row.invoice.unitCount.length > 1 ? t('global.multiple', {count: params.row.invoice.unitCount.length}) : params.row.invoice.unitName,
                  minWidth: 200,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'invoice.period',
                  color: 'secondary',
                  label: t('invoices.bills.mailing.table.period'),
                  valueGetter: (params: GridValueGetterParams<any, InvoiceForMailing>) => t('global.period', {
                    from: new Date(params.row.invoice.from),
                    to: new Date(params.row.invoice.to),
                  }),
                  width: 210,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'address',
                  label: t('invoices.bills.mailing.table.address'),
                  minWidth: 200
                }),
              ]}
              disablePagination
              paginationMode="client"
              disableRowSelection
              handleRowChanged={(param) => {
                updateInvoiceForMailing(param.id, param as InvoiceForMailing);
                return param;
              }}
              data={invoices}/>
            <Stack direction="row" justifyContent="center">
              <SelectComponent
                label={t('invoices.bills.mailing.table.template')}
                items={SendInvoiceType.selectItems()}
                color="secondary"
                handleChange={setSendInvoiceType}
                value={sendInvoiceType}
              />
            </Stack>
          </Stack>
        )}
        actions={(
          <>
            <Button variant="outlined" color="primary" onClick={handleClose} disabled={loading}>
              {t('global.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || invoices.some((i) => !i.emails || i.emails.length == 0)}
              onClick={loadInvoiceReadyToSend}>
              {t('global.validate')}
            </Button>
          </>
        )}/>
      {showConfirm && (
        <ConfirmSendMailModal
          invoices={invoiceReadyToSend ?? []}
          sendInvoiceType={sendInvoiceType}
          handleClose={() => setShowConfirm(false)}
          handleSuccess={handleClose}/>
      )}
    </>
  );
};

export default JobInvoiceDetailsModal;
